/**
 * Tournament sessions persisted in localStorage.
 * Key: hachinosu_tournament_sessions
 */

const STORAGE_KEY = 'hachinosu_tournament_sessions';

function generateId() {
  return `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}

export function getSessions() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const data = JSON.parse(raw);
    return Array.isArray(data) ? data : [];
  } catch {
    return [];
  }
}

export function saveSessions(sessions) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(sessions));
    return true;
  } catch {
    return false;
  }
}

export function createSession({
  tournamentName,
  myLeaderCardId,
  myLeaderName,
  myLeaderImageUrl,
  playerCount,
  finalPlacement,
  sessionType = 'tournament',
  format = null,
}) {
  const sessions = getSessions();
  const session = {
    id: generateId(),
    createdAt: new Date().toISOString(),
    tournamentName: tournamentName || 'Unnamed',
    myLeaderCardId: myLeaderCardId || null,
    myLeaderName: myLeaderName || '',
    myLeaderImageUrl: myLeaderImageUrl || null,
    playerCount: sessionType === 'tournament' ? (playerCount ?? 0) : null,
    finalPlacement: sessionType === 'tournament' ? (finalPlacement ?? null) : null,
    sessionType: sessionType === 'practice' ? 'practice' : 'tournament',
    format: format || null,
    rounds: [],
    deckCode: null,
    deckCards: [],
  };
  sessions.unshift(session);
  saveSessions(sessions);
  return session;
}

export function updateSession(sessionId, updates) {
  const sessions = getSessions();
  const i = sessions.findIndex((s) => s.id === sessionId);
  if (i === -1) return null;
  sessions[i] = { ...sessions[i], ...updates };
  saveSessions(sessions);
  return sessions[i];
}

export function getSession(sessionId) {
  return getSessions().find((s) => s.id === sessionId) || null;
}

export function deleteSession(sessionId) {
  const sessions = getSessions().filter((s) => s.id !== sessionId);
  saveSessions(sessions);
  return true;
}

export function duplicateSession(sessionId) {
  const session = getSession(sessionId);
  if (!session) return null;
  const sessions = getSessions();
  const copy = {
    id: generateId(),
    createdAt: new Date().toISOString(),
    tournamentName: session.tournamentName,
    myLeaderCardId: session.myLeaderCardId,
    myLeaderName: session.myLeaderName,
    myLeaderImageUrl: session.myLeaderImageUrl,
    playerCount: session.playerCount ?? null,
    finalPlacement: session.finalPlacement ?? null,
    sessionType: session.sessionType || 'tournament',
    format: session.format ?? null,
    deckCode: session.deckCode ?? null,
    deckCards: session.deckCards ? [...session.deckCards] : [],
    rounds: (session.rounds || []).map((r) => ({
      id: generateId(),
      enemyLeaderCardId: r.enemyLeaderCardId,
      enemyLeaderName: r.enemyLeaderName,
      enemyLeaderImageUrl: r.enemyLeaderImageUrl,
      won: r.won,
      wonDieRoll: r.wonDieRoll,
      wentFirst: r.wentFirst,
      notes: r.notes ?? '',
      didMulligan: r.didMulligan ?? false,
      openingHandPreMulligan: r.openingHandPreMulligan ? [...r.openingHandPreMulligan] : [],
      openingHandPostMulligan: r.openingHandPostMulligan ? [...r.openingHandPostMulligan] : [],
    })),
  };
  sessions.unshift(copy);
  saveSessions(sessions);
  return copy;
}

export function addRound(sessionId, round) {
  const sessions = getSessions();
  const i = sessions.findIndex((s) => s.id === sessionId);
  if (i === -1) return null;
  const newRound = {
    id: generateId(),
    enemyLeaderCardId: round.enemyLeaderCardId ?? null,
    enemyLeaderName: round.enemyLeaderName ?? '',
    enemyLeaderImageUrl: round.enemyLeaderImageUrl ?? null,
    won: round.won ?? false,
    wonDieRoll: round.wonDieRoll ?? false,
    wentFirst: round.wentFirst ?? false,
    notes: round.notes ?? '',
    didMulligan: round.didMulligan ?? false,
    openingHandPreMulligan: round.openingHandPreMulligan ? [...round.openingHandPreMulligan] : [],
    openingHandPostMulligan: round.openingHandPostMulligan ? [...round.openingHandPostMulligan] : [],
  };
  sessions[i].rounds = [...(sessions[i].rounds || []), newRound];
  saveSessions(sessions);
  return sessions[i];
}

export function updateRound(sessionId, roundId, updates) {
  const sessions = getSessions();
  const i = sessions.findIndex((s) => s.id === sessionId);
  if (i === -1) return null;
  const rounds = sessions[i].rounds || [];
  const rIdx = rounds.findIndex((r) => r.id === roundId);
  if (rIdx === -1) return null;
  rounds[rIdx] = { ...rounds[rIdx], ...updates };
  sessions[i].rounds = [...rounds];
  saveSessions(sessions);
  return sessions[i];
}

export function deleteRound(sessionId, roundId) {
  const sessions = getSessions();
  const i = sessions.findIndex((s) => s.id === sessionId);
  if (i === -1) return null;
  sessions[i].rounds = (sessions[i].rounds || []).filter((r) => r.id !== roundId);
  saveSessions(sessions);
  return sessions[i];
}
